import classNames from 'classnames';
import {
  PropsWithChildren,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

type Props = PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  /**
   * Ref to a container that encloses BOTH the trigger pill and the dropdown
   * panel. Mouse-down events outside this container close the dropdown.
   * Targeting the wrapper (not just the panel) avoids a click-toggle race
   * where mousedown closes and then the pill button's onClick re-opens.
   */
  anchorRef: RefObject<HTMLElement>;
  onApply?: () => void;
  applyLabel?: string;
  /** Renders the panel without the Apply footer (used by the client picker
   *  which provides its own bottom bar). */
  hideApply?: boolean;
  className?: string;
  panelStyle?: React.CSSProperties;
}>;

const FilterDropdown = ({
  open,
  onClose,
  anchorRef,
  onApply,
  applyLabel = 'Apply selection',
  hideApply,
  className,
  panelStyle,
  children,
}: Props) => {
  useEffect(() => {
    if (!open) return;

    const handlePointerOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (anchorRef.current && anchorRef.current.contains(target)) return;
      onClose();
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    // Capture so we beat React's onClick handlers (otherwise another pill's
    // trigger click could fire between mousedown and re-open this one).
    // Touchstart is required for iOS Safari where a tap outside doesn't always
    // synthesise a mousedown before the dropdown's click handlers run.
    document.addEventListener('mousedown', handlePointerOutside, true);
    document.addEventListener('touchstart', handlePointerOutside, true);
    window.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointerOutside, true);
      document.removeEventListener('touchstart', handlePointerOutside, true);
      window.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose, anchorRef]);

  const panelRef = useRef<HTMLDivElement>(null);
  const [alignRight, setAlignRight] = useState(false);
  /** When the panel is wider than either side of the pill, fall back to
   *  viewport-pinned positioning (position: fixed, pinned to both edges). */
  const [fixedTop, setFixedTop] = useState<number | null>(null);

  // Pick the best of three placements after the panel mounts:
  //   1. Default — absolute, anchored to pill's left edge
  //   2. align-right — absolute, anchored to pill's right edge (when it would
  //      overflow the viewport to the right)
  //   3. fixed-pin — position: fixed, pinned to viewport edges (when neither
  //      side of the pill has room to fit the panel; e.g. wide client picker
  //      from a mid-row pill on a phone)
  // Re-runs on window resize so rotating a phone re-measures correctly.
  useLayoutEffect(() => {
    if (!open) {
      setAlignRight(false);
      setFixedTop(null);
      return;
    }
    const measure = () => {
      const panel = panelRef.current;
      const anchor = anchorRef.current;
      if (!panel || !anchor) return;
      const panelWidth = panel.offsetWidth;
      const anchorRect = anchor.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const fitsLeftAnchor = anchorRect.left + panelWidth <= viewportWidth - 8;
      const fitsRightAnchor = anchorRect.right - panelWidth >= 8;
      if (!fitsLeftAnchor && !fitsRightAnchor) {
        setFixedTop(anchorRect.bottom + 6);
        setAlignRight(false);
      } else if (!fitsLeftAnchor) {
        setAlignRight(true);
        setFixedTop(null);
      } else {
        setAlignRight(false);
        setFixedTop(null);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, anchorRef]);

  if (!open) return null;

  const mergedStyle: React.CSSProperties =
    fixedTop !== null ? { ...panelStyle, top: fixedTop } : (panelStyle ?? {});

  return (
    <div
      ref={panelRef}
      className={classNames('dropdown-panel', className, {
        'align-right': alignRight,
        'fixed-pin': fixedTop !== null,
      })}
      style={mergedStyle}
      role="dialog"
    >
      {children}
      {!hideApply && onApply ? (
        <div className="dropdown-footer">
          <button type="button" className="dropdown-apply" onClick={onApply}>
            {applyLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default FilterDropdown;
